import { useState, useRef, useEffect, useCallback, RefObject } from 'react';

import { isRtlEnabled } from '@gem/utils';

interface ICarouselScrollPresenterParams {
  itemWidth: number;
  gap: number;
  itemsToShow: number;
  childrenCount: number;
  activeIndex?: number;
  scrollByClientWidth?: boolean;
  isScrollToActiveOnMount?: boolean;
  onChangeActiveIndex?: (index: number) => void;
}

interface ICarouselScrollPresenterReturn {
  carouselRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  showPrevButton: boolean;
  showNextButton: boolean;
  scrollToIndex: (index: number) => void;
  scrollNext: () => void;
  scrollPrev: () => void;
}

export function useCarouselScrollPresenter({
  itemWidth,
  gap,
  itemsToShow,
  childrenCount,
  activeIndex,
  scrollByClientWidth,
  isScrollToActiveOnMount,
  onChangeActiveIndex,
}: ICarouselScrollPresenterParams): ICarouselScrollPresenterReturn {
  const [showPrevButton, setShowPrevButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(childrenCount > itemsToShow);
  const carouselRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasInitialAutoScrollRef = useRef(false);

  const updateButtonVisibility = useCallback((): void => {
    if (!carouselRef.current || !contentRef.current) {
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = carouselRef.current;
    setShowPrevButton(Math.abs(scrollLeft) > 0);
    setShowNextButton(Math.ceil(scrollLeft) + clientWidth < scrollWidth);
  }, []);

  const scrollToIndex = useCallback(
    (index: number): void => {
      if (!carouselRef.current) {
        return;
      }

      const itemTotalWidth = itemWidth + gap;
      const targetScrollLeft = index * itemTotalWidth;
      carouselRef.current.scrollTo({
        left: isRtlEnabled() ? -targetScrollLeft : targetScrollLeft,
        behavior: 'smooth',
      });
    },
    [itemWidth, gap]
  );

  const isItemVisible = useCallback((index: number): boolean => {
    if (!carouselRef.current || !contentRef.current) {
      return false;
    }

    const item = contentRef.current.children[index] as HTMLElement | undefined;
    if (!item) {
      return false;
    }

    const { scrollLeft, clientWidth } = carouselRef.current;
    const itemLeft = item.offsetLeft;
    const itemRight = itemLeft + item.offsetWidth;
    const viewportLeft = scrollLeft;
    const viewportRight = scrollLeft + clientWidth;

    return itemLeft >= viewportLeft && itemRight <= viewportRight;
  }, []);

  const scrollToActiveOnMountIfNeeded = useCallback((): void => {
    if (activeIndex === undefined || itemWidth <= 0) {
      return;
    }

    if (!isItemVisible(activeIndex)) {
      scrollToIndex(activeIndex);
    }
  }, [activeIndex, itemWidth, scrollToIndex, isItemVisible]);

  const handleScroll = useCallback((): void => {
    updateButtonVisibility();
    if (!carouselRef.current) {
      return;
    }

    const { scrollLeft } = carouselRef.current;
    const itemTotalWidth = itemWidth + gap;
    const currentIndex = Math.round(scrollLeft / itemTotalWidth);
    onChangeActiveIndex?.(currentIndex);
  }, [itemWidth, gap, onChangeActiveIndex, updateButtonVisibility]);

  const scrollClientWidth = (direction: 'next' | 'prev'): void => {
    if (!carouselRef.current) return;

    const { scrollLeft, clientWidth, scrollWidth } = carouselRef.current;
    let targetScrollLeft = scrollLeft + (direction === 'next' ? clientWidth : -clientWidth);
    targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, scrollWidth - clientWidth));

    carouselRef.current.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  };

  const scrollNext = useCallback((): void => {
    if (!carouselRef.current || !contentRef.current) {
      return;
    }

    if (scrollByClientWidth) {
      scrollClientWidth('next');
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = carouselRef.current;
    console.log(scrollLeft, clientWidth, scrollWidth);
    const maxScrollLeft = scrollWidth - clientWidth;
    if (Math.abs(scrollLeft) >= maxScrollLeft) {
      return;
    }

    const itemTotalWidth = itemWidth + gap;
    const currentIndex = Math.round(Math.abs(scrollLeft) / itemTotalWidth);
    const nextIndex = currentIndex + 1;

    if (nextIndex >= childrenCount) {
      return;
    }

    scrollToIndex(nextIndex);
  }, [itemWidth, gap, childrenCount, scrollToIndex]);

  const scrollPrev = useCallback((): void => {
    if (!carouselRef.current || !contentRef.current) {
      return;
    }

    if (scrollByClientWidth) {
      scrollClientWidth('prev');
      return;
    }

    const { scrollLeft } = carouselRef.current;
    const itemTotalWidth = itemWidth + gap;
    const currentIndex = Math.round(Math.abs(scrollLeft) / itemTotalWidth);
    const prevIndex = Math.max(currentIndex - 1, 0);

    scrollToIndex(prevIndex);
  }, [itemWidth, gap, scrollToIndex]);

  useEffect(() => {
    carouselRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      carouselRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    updateButtonVisibility();
  }, [childrenCount, itemsToShow, itemWidth, gap, updateButtonVisibility]);

  useEffect(() => {
    if (!isScrollToActiveOnMount && activeIndex !== undefined) {
      scrollToIndex(activeIndex);
    }
  }, [activeIndex, isScrollToActiveOnMount, scrollToIndex]);

  useEffect(() => {
    if (!isScrollToActiveOnMount || hasInitialAutoScrollRef.current) {
      return;
    }

    if (activeIndex === undefined || itemWidth <= 0) {
      return;
    }

    scrollToActiveOnMountIfNeeded();
    hasInitialAutoScrollRef.current = true;
  }, [isScrollToActiveOnMount, activeIndex, itemWidth, scrollToActiveOnMountIfNeeded]);

  return {
    carouselRef,
    contentRef,
    showPrevButton,
    showNextButton,
    scrollToIndex,
    scrollNext,
    scrollPrev,
  };
}
